"use client";
import { useEffect } from "react";
import {
  atom,
  selector,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { ImageFragment, PostPreview } from "../article-card";

interface PostsFilterProps {
  posts: PostPreview[];
}

export type PostsFilterOption =
  | "All"
  | "Business Insights"
  | "Industrial Hazards"
  | "Infrastructure"
  | "Natural Hazards"
  | "Pollution"
  | "Products";

export type PostsSortOption =
  | "Alphabetical"
  | "Newest to Oldest"
  | "Oldest to Newest"
  | "Sort";
export const filterOptions: PostsFilterOption[] = [
  "All",
  "Business Insights",
  "Industrial Hazards",
  "Infrastructure",
  "Natural Hazards",
  "Pollution",
  "Products",
];
export const sortOptions: PostsSortOption[] = [
  "Alphabetical",
  "Newest to Oldest",
  "Oldest to Newest",
];

export interface PressItem {
  __typename: "GCMS_PressItem";
  title: React.ReactNode;
  description: React.ReactNode;
  date?: any | null;
  type:
    | "blog"
    | "in_the_news"
    | "news"
    | "news_brief"
    | "podcast"
    | "press_release"
    | "recent_press"
    | "release"
    | "report"
    | "white_paper";
  info?: { __typename: "GCMS_PressItem_Link"; link: string } | null;
  logo?: ImageFragment & { alt?: string };
  tags?: string[];
}

export interface PressPostFilterProps {
  pressItems: PressItem[];
}

export type PressPostsFilterOption =
  | "All"
  | "In the News"
  | "Press Release"
  | "Podcast"
  | "Blog"
  | "White Paper"
  | "";

export const pressFilterOptions: PressPostsFilterOption[] = [
  "All",
  "In the News",
  "Press Release",
  "Podcast",
  "Blog",
  "White Paper",
];

export interface PartialPostsProps {
  __typename: "GCMS_Post";
  excerpt: string;
  title: string;
  slug: string;
  blogCoverImage: any;
  aggregatePostPage: {
    title: string;
  };
  date: string;
}
export interface PartialPressItemProps {
  __typename: "GCMS_PressItem";
  title: string;
  description: string;
  logo: any;
  type: string;
  info: {
    link: string;
  };
  date: string;
}

export const postsControllerAtom = atom<{
  search: string;
  filter: PostsFilterOption;
  sort: PostsSortOption;
}>({
  key: "usePostsSearchValue",
  default: {
    search: "",
    filter: "All",
    sort: "Sort",
  },
});

export const pressPostsControllerAtom = atom<{
  filter: PressPostsFilterOption;
  sort: PostsSortOption;
}>({
  key: "usePressPostsSearchValue",
  default: {
    filter: "All",
    sort: "Sort",
  },
});

const finalResultAtom = atom<
  PostPreview[] | PressItem[] | (PartialPostsProps | PartialPressItemProps)[]
>({
  key: "usePostsResults",
  default: [],
});

const postsSelector = selector({
  key: "postsSelector",
  // All the search selector stuff here without the set portion. You can also add the filter and sort stuff here as well!
  // On the get portion
  get: ({ get }) => {
    let controller;
    const finalResult = get(finalResultAtom);
    // First filter the posts by the controller.filter value
    // Then filter the posts by the controller.search value
    // Finally, sort the posts by the controller.sort value and return the result
    let filterArr:
      | PostPreview[]
      | PressItem[]
      | (PartialPostsProps | PartialPressItemProps)[] = finalResult;

    const isPress =
      (filterArr as PressItem[])?.[0]?.__typename === "GCMS_PressItem";

    if (isPress) {
      controller = get(pressPostsControllerAtom);
    } else {
      controller = get(postsControllerAtom);
    }

    // Filter by controller.filter
    if (isPress) {
      filterArr =
        controller.filter !== "All"
          ? (filterArr as PressItem[]).filter(
              (post) =>
                post.type.replace(/_/g, " ") ===
                controller.filter.toLowerCase(),
            )
          : filterArr;
    } else {
      filterArr =
        controller.filter !== "All"
          ? (filterArr as PostPreview[]).filter(
              (post) => post.aggregatePostPage?.title === controller.filter,
            )
          : filterArr;
    }

    // Filter by controller.search
    if (!isPress) {
      filterArr = (filterArr as PostPreview[]).filter(
        (post) =>
          post.title.toLowerCase().includes(
            (
              controller as {
                search: string;
                filter: PostsFilterOption;
                sort: PostsSortOption;
              }
            ).search.toLowerCase(),
          ) ||
          post.excerpt?.toLowerCase().includes(
            (
              controller as {
                search: string;
                filter: PostsFilterOption;
                sort: PostsSortOption;
              }
            ).search.toLowerCase(),
          ) ||
          post.author?.person?.name.toLowerCase().includes(
            (
              controller as {
                search: string;
                filter: PostsFilterOption;
                sort: PostsSortOption;
              }
            ).search.toLowerCase(),
          ),
      );
    }

    // Sort by controller.sort
    switch (
      (
        controller as
          | {
              search: string;
              filter: PostsFilterOption;
              sort: PostsSortOption;
            }
          | {
              filter: PressPostsFilterOption;
              sort: PostsSortOption;
            }
      ).sort
    ) {
      case "Alphabetical":
        return !isPress
          ? filterArr.sort((a, b) =>
              (a?.title as string)?.localeCompare(b.title as string),
            )
          : [...filterArr].sort((a, b) =>
              String(a.title).localeCompare(String(b.title)),
            );
      case "Newest to Oldest":
        return !isPress
          ? (filterArr as PostPreview[]).sort(
              (a, b) =>
                new Date(b.published).getTime() -
                new Date(a.published).getTime(),
            )
          : [...(filterArr as PressItem[])].sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
            );
      case "Oldest to Newest":
        return !isPress
          ? (filterArr as PostPreview[]).sort(
              (a, b) =>
                new Date(a.published).getTime() -
                new Date(b.published).getTime(),
            )
          : [...(filterArr as PressItem[])].sort(
              (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
            );
      case "Sort":
        if (!isPress) {
          (filterArr as PostPreview[]).sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          );
        } else {
          const trendingPosts = (filterArr as PressItem[])
            .filter((post) => post?.tags?.includes("featured"))
            .sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
            );
          const otherPosts = (filterArr as PressItem[])
            .filter((post) => !post?.tags?.includes("featured"))
            .sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
            );
          filterArr = trendingPosts.concat(otherPosts);
        }
    }
    return filterArr;
  },
});

export const highlightedSearchAtom = atom<string>({
  key: "usePostsHighlightQuery",
  default: "",
});

export function usePostsFilter(
  prop:
    | PostsFilterProps
    | PressPostFilterProps
    | (PartialPostsProps | PartialPressItemProps)[],
  from: string,
  preselectedFilter?: string,
) {
  const setPosts = useSetRecoilState(finalResultAtom);
  const [controller, setPostsController] = useRecoilState(postsControllerAtom);
  const [pressController, setPressPostsController] = useRecoilState(
    pressPostsControllerAtom,
  );

  const selectedPosts: any = useRecoilValue(postsSelector);
  const setHighlightQuery = useSetRecoilState(highlightedSearchAtom);

  useEffect(() => {
    setPosts(
      from === "news-room"
        ? (prop as PressPostFilterProps)?.pressItems
        : from === "knowledge-center"
          ? (prop as PostsFilterProps)?.posts
          : (prop as (PartialPostsProps | PartialPressItemProps)[]),
    );
  }, [prop]);

  useEffect(() => {
    if (from === "news-room" && preselectedFilter) {
      setPressPostsController({
        ...pressController,
        filter: preselectedFilter as PressPostsFilterOption,
      });
    }
  }, [preselectedFilter]);

  const onSortChange = (e: any) => {
    from === "news-room"
      ? setPressPostsController({
          ...pressController,
          sort: e.target.value as PostsSortOption,
        })
      : from === "knowledge-center"
        ? setPostsController({
            ...controller,
            sort: e.target.value as PostsSortOption,
          })
        : "";
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPostsController({
      ...controller,
      search: e.target.value,
    });
    setHighlightQuery(e.target.value);
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLButtonElement>) => {
    from === "news-room"
      ? setPressPostsController({
          ...pressController,
          filter: e.target.value as PressPostsFilterOption,
        })
      : setPostsController({
          ...controller,
          filter: e.target.value as PostsFilterOption,
        });
  };

  return {
    onSortChange,
    onFilterChange,
    onSearchChange,
    selectedPosts,
    filterOptions:
      from === "news-room"
        ? pressFilterOptions.slice(1)
        : filterOptions.slice(1),
    sortOptions,
  };
}
